import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import {
  ArrowDownToLine,
  ArrowRight,
  Package,
  Truck,
  CheckCircle2,
  ChevronRight,
  Info,
  AlertCircle,
  Clock,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { adjustStock, StockApiError, stockErrorToGerman } from '@/lib/stockApi';
import { useAuth } from '@/api/AuthContext';

export default function WarehouseWareneingang() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [deliveredAmounts, setDeliveredAmounts] = useState({}); // { orderId: amount }
  const [acceptedAmounts, setAcceptedAmounts] = useState({}); // { orderId: amount }

  // 1. Fetch Products and Purchase Orders
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: purchaseOrders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['purchase_orders'],
    queryFn: () => base44.entities.PurchaseOrder.list(),
  });

  // 2. Filter for "bestellt" status and group by supplier
  const activeOrders = useMemo(() => {
    return purchaseOrders.filter(o => o.status === 'bestellt');
  }, [purchaseOrders]);

  const groupedBySupplier = useMemo(() => {
    const groups = {};
    activeOrders.forEach(order => {
      const supplierName = order.supplier_name || 'Kein Lieferant';
      const product = products.find(p => p.id === order.product_id);
      
      if (!groups[supplierName]) {
        groups[supplierName] = {
          name: supplierName,
          items: []
        };
      }
      groups[supplierName].items.push({
        ...order,
        product
      });
    });
    return Object.values(groups);
  }, [activeOrders, products]);

  // 3. Completion Mutation
  const completeMutation = useMutation({
    mutationFn: async ({ order, delivered, accepted }) => {
      const product = products.find(p => p.id === order.product_id);
      if (!product) throw new Error("Produkt nicht gefunden");

      const deliveredInt = Number.parseInt(delivered, 10);
      const acceptedInt = Number.parseInt(accepted, 10);

      if (!Number.isFinite(deliveredInt) || deliveredInt < 0) {
        throw new Error("Gelieferte Menge ist ungültig.");
      }
      if (!Number.isFinite(acceptedInt) || acceptedInt < 0) {
        throw new Error("Angenommene Menge ist ungültig.");
      }
      if (acceptedInt > deliveredInt) {
        throw new Error("Angenommene Menge darf die gelieferte Menge nicht überschreiten.");
      }

      // Stok girişi adjust_stock RPC ile — atomic güncelleme +
      // stock_movements audit kaydı (type='wareneingang', source_table
      // ='purchase_orders'). Kabul edilen miktar 0 olursa atlıyoruz
      // çünkü delta=0 RPC tarafında hata verir.
      if (acceptedInt > 0) {
        try {
          await adjustStock({
            productId: product.id,
            variantValue: null,
            delta: acceptedInt,
            type: 'wareneingang',
            reason: `Lieferung von ${order.supplier_name || 'Lieferant'}`,
            actorEmail: user?.email,
            sourceTable: 'purchase_orders',
            sourceId: order.id,
          });
        } catch (err) {
          if (err instanceof StockApiError) {
            throw new Error(stockErrorToGerman(err));
          }
          throw err;
        }
      }

      // Update purchase_order status and quantities
      return base44.entities.PurchaseOrder.update(order.id, {
        delivered_quantity: deliveredInt,
        accepted_quantity: acceptedInt,
        status: 'abgeschlossen'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      queryClient.invalidateQueries(['purchase_orders']);
      toast.success("Wareneingang abgeschlossen — Lagerbestand aktualisiert.");
    },
    onError: (err) => toast.error("Fehler: " + err.message)
  });

  const handleInputChange = (orderId, field, val) => {
    if (field === 'delivered') {
      setDeliveredAmounts(prev => ({ ...prev, [orderId]: val }));
      // If accepted is empty, auto-fill it with delivered value for speed
      if (!acceptedAmounts[orderId]) {
        setAcceptedAmounts(prev => ({ ...prev, [orderId]: val }));
      }
    } else {
      setAcceptedAmounts(prev => ({ ...prev, [orderId]: val }));
    }
  };

  const handleComplete = (order) => {
    const deliveredRaw = deliveredAmounts[order.id] ?? order.ordered_quantity;
    const acceptedRaw = acceptedAmounts[order.id] ?? order.ordered_quantity;

    const delivered = Number.parseInt(deliveredRaw, 10);
    const accepted = Number.parseInt(acceptedRaw, 10);

    if (!Number.isFinite(delivered) || delivered < 0) {
      toast.error("Bitte eine gültige gelieferte Menge angeben.");
      return;
    }
    if (!Number.isFinite(accepted) || accepted < 0) {
      toast.error("Bitte eine gültige angenommene Menge angeben.");
      return;
    }
    if (accepted > delivered) {
      toast.error("Angenommene Menge darf die gelieferte Menge nicht überschreiten.");
      return;
    }

    completeMutation.mutate({ order, delivered, accepted });
  };

  const isLoading = productsLoading || ordersLoading;

  if (isLoading) {
    return (
      <div className="p-10 text-center text-slate-400">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-emerald-500" />
        <p>Lade Wareneingänge...</p>
      </div>
    );
  }

  return (
    <PageShell
      head={(
          <div className="flex items-center gap-2 mb-2 justify-between">
            <div className="flex items-center gap-2">
              <PageHeader icon={<ArrowDownToLine className="w-5 h-5" />} title="Wareneingang" />
            </div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-100 px-3 py-1">
              {activeOrders.length} Offene Positionen
            </Badge>
          </div>
      )}
    >

      {/* Supplier Groups */}
      <div className="space-y-8">
        {groupedBySupplier.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="p-12 text-center text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-20 text-slate-500" />
              <p className="font-medium">Keine erwarteten Lieferungen.</p>
              <p className="text-xs">Markieren Sie Produkte im 'Dispo' Tab als 'Bestellt', um sie hier zu sehen.</p>
            </CardContent>
          </Card>
        ) : (
          groupedBySupplier.map((supplier) => (
            <div key={supplier.name} className="space-y-4">
              <div className="flex items-center gap-2 px-1">
                <Truck className="w-5 h-5 text-emerald-600" />
                <h2 className="font-bold text-slate-800 uppercase tracking-tight text-sm">
                  Lieferung von: {supplier.name}
                </h2>
                <Badge className="bg-slate-100 text-slate-600 text-[10px]">{supplier.items.length} Positionen</Badge>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {supplier.items.map((item) => {
                  const delivered = deliveredAmounts[item.id] !== undefined ? deliveredAmounts[item.id] : '';
                  const accepted = acceptedAmounts[item.id] !== undefined ? acceptedAmounts[item.id] : '';
                  const diff = (parseInt(delivered) || 0) - (parseInt(accepted) || 0);

                  return (
                    <Card key={item.id} className="overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row">
                        {/* Product Info */}
                        <div className="p-4 md:w-1/3 flex items-center gap-4 bg-slate-50/50 border-r border-slate-100">
                          <div className="w-12 h-12 rounded-xl border bg-white flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                            {item.product?.image_url ? (
                              <img loading="lazy" decoding="async" src={item.product.image_url} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <Package className="w-6 h-6 text-slate-300" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 truncate">{item.product?.name || 'Unbekanntes Produkt'}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-[9px] font-mono h-4 uppercase">{item.product?.sku || 'KEINE SKU'}</Badge>
                              <span className="text-[10px] text-slate-400 font-medium">Lager: {item.product?.stock || 0}</span>
                            </div>
                          </div>
                        </div>

                        {/* Controls */}
                        <div className="p-4 flex-1 flex flex-wrap items-center justify-between gap-6">
                          <div className="flex items-center gap-8">
                            {/* Ordered Qty (Read-only) */}
                            <div className="text-center">
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Bestellt</p>
                              <div className="text-base font-black text-slate-900 bg-slate-100 px-3 py-1 rounded-lg">
                                {item.ordered_quantity}
                              </div>
                            </div>

                            <ArrowRight className="w-4 h-4 text-slate-300 hidden md:block" />

                            {/* Delivered Qty (Input) */}
                            <div className="text-center">
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Geliefert</p>
                              <Input 
                                type="number"
                                placeholder="0"
                                className="w-20 h-9 text-center font-bold focus:border-emerald-500"
                                value={delivered}
                                onChange={(e) => handleInputChange(item.id, 'delivered', e.target.value)}
                              />
                            </div>

                            {/* Accepted Qty (Input) */}
                            <div className="text-center">
                              <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest mb-1">Angenommen</p>
                              <Input 
                                type="number"
                                placeholder="0"
                                className={cn(
                                  "w-20 h-9 text-center font-black focus:border-emerald-500",
                                  diff > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50/30"
                                )}
                                value={accepted}
                                onChange={(e) => handleInputChange(item.id, 'accepted', e.target.value)}
                              />
                            </div>

                            {/* Difference display */}
                            {diff > 0 && (
                              <div className="flex items-center gap-1.5 text-amber-600 animate-in fade-in slide-in-from-left-2">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-[10px] font-black uppercase">-{diff} Differenz</span>
                              </div>
                            )}
                          </div>

                          <Button 
                            disabled={!delivered || !accepted || completeMutation.isPending}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-2 font-bold uppercase text-[10px] px-6 h-10 shadow-lg shadow-slate-200"
                            onClick={() => handleComplete(item)}
                          >
                            {completeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            Abschließen
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-slate-100/80 rounded-2xl p-4 flex items-start gap-3 border border-slate-200">
        <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-500 leading-relaxed">
          <p className="font-bold text-slate-700 uppercase mb-1">Hinweis zum Wareneingang:</p>
          <p>Das Feld <strong>Angenommen</strong> bestimmt die Menge, die tatsächlich in den Lagerbestand übernommen wird. Differenzen zwischen gelieferter und angenommener Menge können für die Qualitätskontrolle dokumentiert werden. Der Lagerbestand wird erst nach Klick auf "Abschließen" aktualisiert.</p>
        </div>
      </div>
    </PageShell>
  );
}
